import { type Ref, ref, watch } from 'vue'

export type RowsModel<TRow> = {
    /** Editable rows. Mutating a row's field is enough: the model emits the new list. */
    rows: Ref<TRow[]>
    showJson: Ref<boolean>
    jsonText: Ref<string>
    jsonError: Ref<string | null>
    append: (row: TRow) => void
    removeAt: (index: number) => void
    /** Structured → JSON pre-fills the box from the rows; JSON → structured parses it back into rows. */
    toggleJson: () => void
}

/**
 * Keeps a list-shaped `modelValue` (a task's inputs, gates or successors) editable as rows instead of
 * hand-written JSON: every row change emits a fresh list, and the JSON toggle round-trips the same data
 * through a textarea so a power user can paste a whole list without losing what the rows already hold.
 *
 * `toRow`/`toValue` let a row hold what an `<input>` actually stores (strings) while the emitted value
 * keeps the contract's shape (numbers, omitted optionals).
 */
export function useRowsModel<TRow, TValue>(options: {
    modelValue: () => TValue[]
    emit: (value: TValue[]) => void
    toRow: (value: TValue) => TRow
    toValue: (row: TRow) => TValue
}): RowsModel<TRow> {
    const rows = ref<TRow[]>([]) as Ref<TRow[]>
    const showJson = ref(false)
    const jsonText = ref('')
    const jsonError = ref<string | null>(null)

    function value(): TValue[] {
        return rows.value.map(row => options.toValue(row))
    }

    function fill(next: TValue[]): void {
        rows.value = next.map(entry => options.toRow(entry))
    }

    fill(options.modelValue())

    // The parent can refill the list (loading another version, a draft restore); rows follow it, and the
    // string compare keeps that from fighting with the emit below.
    watch(
        options.modelValue,
        next => {
            if (JSON.stringify(next) !== JSON.stringify(value())) {
                fill(next)
            }
        },
        { deep: true },
    )

    watch(
        rows,
        () => {
            options.emit(value())
        },
        { deep: true },
    )

    function toggleJson(): void {
        if (!showJson.value) {
            jsonText.value = JSON.stringify(value(), null, 2)
            jsonError.value = null
            showJson.value = true
            return
        }
        let parsed: unknown
        try {
            parsed = JSON.parse(jsonText.value)
        } catch {
            jsonError.value = 'That is not valid JSON, so the rows are unchanged.'
            return
        }
        if (!Array.isArray(parsed)) {
            jsonError.value = 'Expected a JSON list, so the rows are unchanged.'
            return
        }
        fill(parsed as TValue[])
        jsonError.value = null
        showJson.value = false
    }

    return {
        rows,
        showJson,
        jsonText,
        jsonError,
        append: row => {
            rows.value = [...rows.value, row]
        },
        removeAt: index => {
            rows.value = rows.value.filter((_, at) => at !== index)
        },
        toggleJson,
    }
}
